import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CONTACT } from "@/lib/site";
import { Reveal } from "@/lib/motion";

const inputCls =
    "w-full bg-transparent border-b border-ink/30 focus:border-ink outline-none py-3 text-base placeholder:text-inkdim/60 transition-colors duration-300";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", project_type: "", message: "" });
    const [sending, setSending] = useState(false);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await api.post("/contact", form);
            toast.success("Message sent — I'll get back to you soon.");
            setForm({ name: "", email: "", project_type: "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    const links = [
        { label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
        { label: "Phone", value: CONTACT.phone, href: null },
        { label: "Location", value: CONTACT.location, href: null },
    ];

    return (
        <section id="contact" className="px-5 md:px-10 py-24 md:py-32 border-t border-line bg-surface/60">
            <Reveal>
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-inkdim">Contact</p>
                <h2 className="mt-6 max-w-5xl font-display font-extrabold uppercase tracking-[-0.02em] leading-[0.95] text-4xl md:text-7xl">
                    Let's make something{" "}
                    <em className="font-serif italic font-medium normal-case">worth watching.</em>
                </h2>
            </Reveal>

            <div className="mt-14 grid grid-cols-12 gap-y-10 md:gap-16">
                <form data-testid="contact-form" onSubmit={submit} className="col-span-12 md:col-span-7 min-w-0 space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <input
                            data-testid="contact-input-name"
                            required
                            value={form.name}
                            onChange={set("name")}
                            placeholder="Your name"
                            className={inputCls}
                        />
                        <input
                            data-testid="contact-input-email"
                            required
                            type="email"
                            value={form.email}
                            onChange={set("email")}
                            placeholder="Email address"
                            className={inputCls}
                        />
                    </div>
                    <select
                        data-testid="contact-input-type"
                        value={form.project_type}
                        onChange={set("project_type")}
                        className={inputCls + " appearance-none cursor-pointer"}
                    >
                        <option value="">What are we making? (optional)</option>
                        {[
                            "Advertising film",
                            "Brand film",
                            "Corporate film",
                            "Product film",
                            "Music video",
                            "Short film",
                            "Fashion & lifestyle",
                            "Something else",
                        ].map((o) => (
                            <option key={o} value={o}>
                                {o}
                            </option>
                        ))}
                    </select>
                    <textarea
                        data-testid="contact-input-message"
                        required
                        rows={4}
                        value={form.message}
                        onChange={set("message")}
                        placeholder="Tell me about the idea, timeline and budget range…"
                        className={inputCls + " resize-none"}
                    />
                    <button
                        data-testid="contact-submit"
                        type="submit"
                        disabled={sending}
                        className="inline-flex items-center gap-3 bg-ink text-paper text-xs font-bold uppercase tracking-[0.2em] px-8 py-4 hover:bg-inkdim disabled:opacity-50 transition-all duration-300 cursor-pointer"
                    >
                        {sending ? "Sending…" : "Send message"} <ArrowUpRight size={14} />
                    </button>
                </form>

                <div className="col-span-12 md:col-span-5 min-w-0">
                    {links.map((l, i) => (
                        <Reveal key={l.label} delay={i * 0.05}>
                            <div className="border-t border-ink/20 py-5">
                                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-inkdim">
                                    {l.label}
                                </p>
                                {l.href ? (
                                    <a
                                        data-testid={`contact-link-${l.label.toLowerCase()}`}
                                        href={l.href}
                                        target={l.href.startsWith("http") ? "_blank" : undefined}
                                        rel="noreferrer"
                                        className="mt-2 inline-flex items-center gap-2 font-display font-extrabold tracking-tight text-xl md:text-2xl hover:opacity-60 transition-opacity duration-300"
                                    >
                                        {l.value} <ArrowUpRight size={18} />
                                    </a>
                                ) : (
                                    <p className="mt-2 font-display font-extrabold tracking-tight text-xl md:text-2xl">
                                        {l.value}
                                    </p>
                                )}
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}
